import {
  persistBoPreference,
  readBoPreferenceRaw,
} from "@/lib/user-preferences/store";
import { createContext, useCallback, useContext, useState } from "react";
import { visibleT } from "@/lib/cms/cms-new-i18n";

const LAST_ORDERS_CHECKED_KEY = "lastTimeNewOrdersChecked";

type LastTimeNewOrdersCheckedContextType = {
  lastChecked: Date;
  setLastChecked: (date: Date) => void;
};

const LastTimeNewOrdersCheckedContext = createContext<
  LastTimeNewOrdersCheckedContextType | undefined
>(undefined);

export const LastTimeNewOrdersCheckedProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const getDefaultLastChecked = () => {
    const raw =
      readBoPreferenceRaw(LAST_ORDERS_CHECKED_KEY) ??
      localStorage.getItem(LAST_ORDERS_CHECKED_KEY);
    const dateFetched = new Date(raw ?? 0);
    return Number.isNaN(dateFetched.getTime()) ? new Date(0) : dateFetched;
  };

  const [innerLastChecked, setInnerLastChecked] = useState<Date>(
    getDefaultLastChecked,
  );

  const setLastChecked = useCallback((date: Date) => {
    persistBoPreference(LAST_ORDERS_CHECKED_KEY, date.toISOString());
    setInnerLastChecked(date);
  }, []);

  return (
    <LastTimeNewOrdersCheckedContext.Provider
      value={{ lastChecked: innerLastChecked, setLastChecked }}
    >
      {children}
    </LastTimeNewOrdersCheckedContext.Provider>
  );
};

export const useLastTimeNewOrdersChecked =
  (): LastTimeNewOrdersCheckedContextType => {
    const context = useContext(LastTimeNewOrdersCheckedContext);
    if (!context) {
      throw new Error(
        visibleT(
          "bo.order.useLastTimeNewOrdersCheckedProviderError",
          "useLastTimeNewOrdersChecked doit être utilisé dans un LastTimeNewOrdersCheckedProvider",
        ),
      );
    }
    return context;
  };
